import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { Sparkle, ArrowRight, ArrowSquareOut, Quotes, HeartStraight, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const PORTRAIT = "/founder/portrait.jpg";
const IMG_SPICE = "/founder/spicenflavor.jpg";
const IMG_FOOD = "/founder/foodpulse.webp";

// body item = string (paragraph) | {list:[...]} | {quote:"..."} | {image, caption}
const SECTIONS = [
  ["His Vision", [
    "Tom's vision is not limited to one company, profession or industry. It is a belief that seemingly unrelated ideas can be connected to create something new.",
    { list: ["Food can connect people", "Technology can connect communities", "Travel can connect cultures", "Family history can connect generations", "Nature can connect humanity to the planet", "Information can empower people"] },
    "His long-term vision is to keep creating useful platforms, businesses, services, content and communities that turn ideas into practical tools for people.",
    { quote: "To imagine a better possibility, connect people and ideas, and turn those possibilities into practical creations that make life more connected, informed, sustainable and meaningful." },
  ]],
  ["The Philosophy: Imagination Before Limitation", [
    "Tom's interpretation of Einstein's philosophy is simple. Don't begin with “Can this be done?” Begin with: “What if it could be done?”",
    "Only after imagining the possibility should we ask: What knowledge do we need? What technology is available? What resources are required? What problems must be solved? Who can help? How can the idea become practical?",
    "This philosophy allows imagination to become the starting point, while knowledge, experience, experimentation and persistence become the tools for turning imagination into reality.",
    { quote: "Imagination + Knowledge + Persistence = Possibility.  Possibility + Action = Creation." },
  ]],
  ["From Food to Ideas", [
    "Tom's entrepreneurial journey illustrates this philosophy. Spice n Flavor began in 2000 as an Indian spice-blend enterprise in New York, introducing multiple blends under the brand India's Spice n Flavor. Later he opened the Spice n Flavor restaurant in San Jose and pursued food delivery and culinary ventures.",
    "These experiences demonstrate an important part of his philosophy: an idea doesn't have to remain in one form. A spice blend can become a brand. A restaurant can become a community. A chef can become an entrepreneur. And a challenge can become the starting point for the next idea.",
    { image: IMG_SPICE, caption: "Spice n Flavor — Flavorful India" },
  ]],
  ["Food, Community & Reducing Waste", [
    "Tom's vision extends beyond preparing and selling food. He sees food as one of the greatest opportunities to connect farmers, producers, restaurants, stores, families, communities and charities.",
    "The goal is not simply to ask “What food can we sell?” but also: “What food can we save?” “What food can we share?” “How can technology reduce waste?” This thinking is central to the broader FoodPulse concept.",
    { image: IMG_FOOD, caption: "FoodPulse — nourishing communities" },
  ]],
  ["Building Connections", [
    "A recurring theme in Tom's ideas is connection — connecting people with people, families with their history, students with classmates and teachers, travelers with airlines and airports, and communities with surplus food.",
    "This is why his projects can appear diverse while sharing a common foundation. The subject changes. The philosophy does not.",
  ]],
  ["Travel & Exploration", [
    "Travel represents another expression of imagination. Every journey begins with an imagined destination: “What would it be like to go there?”",
    "Tom's vision for TravelerPulse and travel-related projects is to make that journey more informed and organized — helping travelers research airlines, airports, destinations, travel requirements, packing, safety, transportation and their own travel history. The objective isn't simply to provide information; it is to help people imagine, plan, experience and remember their journeys.",
  ]],
  ["Preserving People & Memories", [
    "Another expression of this philosophy is the belief that technology should preserve things that might otherwise disappear — family histories, school memories, friendships, photographs, stories, recipes, places, journeys and experiences.",
    "Tom's projects involving family and alumni connections reflect the belief that technology should not only create the future — it should also preserve the past.",
  ]],
  ["Why AlumniPulse", [
    "AlumniPulse is where this philosophy meets education. It brings together schools, colleges, universities, students, teachers, classmates and alumni into one connected global network.",
    "The idea is simple: the people who shaped our journey should never become strangers simply because time passed. AlumniPulse gives those connections a place to live — a way to find a lost classmate, reconnect, rediscover shared memories, plan reunions, and even remember those who are no longer with us.",
    { list: ["Find a lost classmate or teacher", "Reconnect and keep the conversation going", "Rediscover and preserve shared memories", "Plan reunions with RSVPs", "Honor those who have passed with dedicated memorials", "Give every institution — big or small — a living digital home"] },
    "It is the same guiding question applied to education: “What if reconnecting with the people who shaped your life could be as simple as remembering where you went to school?”",
  ]],
  ["Entrepreneurship as an Experiment", [
    "Tom sees entrepreneurship less as a straight road and more as a continuous experiment. An idea may work, grow, change, fail, teach — and reappear in another form.",
    "A discontinued project is not necessarily a failure. It can become knowledge for the next idea. Every obstacle reveals a constraint, every constraint can inspire another solution, and every solution can create another possibility.",
  ]],
  ["His Mission", [
    "Tom's mission is to transform imagination into useful reality.",
    { list: ["Imagine beyond conventional boundaries", "Identify problems that affect everyday people", "Connect ideas that are normally treated separately", "Use technology to make solutions accessible", "Create platforms that encourage participation", "Preserve knowledge, memories and experiences", "Reduce waste and encourage responsible use of resources", "Connect people, communities and cultures", "Learn continuously from experimentation", "Leave behind useful ideas and tools that can keep evolving"] },
  ]],
];

const PRINCIPLES = [
  ["Imagine First", "Don't allow today's limitations to define tomorrow's possibilities."],
  ["Learn Continuously", "Knowledge provides the tools necessary to turn ideas into reality."],
  ["Connect the Unconnected", "The best ideas often emerge when two unrelated concepts are brought together."],
  ["Build for People", "Technology has value when it makes people's lives better."],
  ["Experiment", "Ideas become meaningful only when someone is willing to try."],
  ["Learn From Failure", "A failed experiment can be valuable knowledge for the next attempt."],
  ["Think Beyond the Present", "Build things that may become more useful tomorrow than they are today."],
  ["Share Knowledge", "Ideas become more powerful when other people can build upon them."],
  ["Preserve What Matters", "People, memories, culture, food, nature and knowledge deserve to be documented."],
  ["Never Stop Imagining", "There is always another problem to solve and another possibility to explore."],
];

const VENTURES = [
  { label: "AlumniPulse.net", mark: "™", href: "https://alumnipulse.net", tag: "Alumni & education network" },
  { label: "TravelerPulse.pro", mark: "™", href: "https://travelerpulse.pro/about", tag: "Travel & transportation community" },
];

export default function Founder() {
  useSeo(
    "Meet the Founder — Tom (Thomas) Vellaringattu",
    "Tom (Thomas) Vellaringattu, founder of AlumniPulse. Imagine what could be. Then build it — his philosophy, vision and journey from food to community, travel and preserving memories.",
    PORTRAIT
  );
  const { user } = useAuth();

  return (
    <article data-testid="founder-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-secondary/40" />
        </div>
        <div className="relative mx-auto grid max-w-5xl items-center gap-10 px-5 py-20 text-primary-foreground sm:py-24 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="overline mb-3 text-secondary-foreground/90">Founder — AlumniPulse™</p>
            <h1 className="font-heading text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Imagine What Could Be.<br />Then Build It.
            </h1>
            <p className="mt-6 max-w-xl text-base text-primary-foreground/85 sm:text-lg">
              Tom (Thomas) Vellaringattu believes meaningful progress begins with a willingness to imagine something that does not yet exist.
            </p>
            <p className="mt-4 font-heading text-lg font-light italic text-secondary">
              “Imagination is more important than knowledge. Knowledge is limited. Imagination encircles the world.” — Albert Einstein
            </p>
          </div>
          <div className="mx-auto w-full max-w-xs">
            <div className="overflow-hidden rounded-3xl border-4 border-primary-foreground/15 shadow-2xl">
              <img src={PORTRAIT} alt="Tom (Thomas George) Vellaringattu" className="aspect-square w-full object-cover" data-testid="founder-portrait" />
            </div>
            <p className="mt-3 text-center text-sm text-primary-foreground/80">Tom (Thomas George) Vellaringattu</p>
            <p className="text-center text-xs text-primary-foreground/60">Founder</p>
          </div>
        </div>
      </section>

      {/* Opening */}
      <section className="mx-auto max-w-2xl px-5 py-14">
        <div className="space-y-4 text-lg leading-relaxed text-foreground/90">
          <p>For Tom, this does not mean knowledge is unimportant — quite the opposite. Knowledge provides the foundation, but imagination provides the direction.</p>
          <p>Knowledge tells us what has been done. Imagination asks what could be done. Knowledge helps us understand the world as it is; imagination gives us the courage to envision the world as it could be.</p>
          <p className="font-heading text-xl font-light italic text-secondary">
            To imagine, create and connect ideas that can make everyday life better for people, communities and the world around us.
          </p>
        </div>
      </section>

      {/* Sections */}
      <div className="mx-auto max-w-2xl px-5 pb-4">
        <div className="space-y-12">
          {SECTIONS.map(([heading, blocks], i) => (
            <section key={i} data-testid={`founder-section-${i}`}>
              <h2 className="font-heading text-2xl font-medium tracking-tight text-primary">{heading}</h2>
              <div className="mt-4 space-y-4">
                {blocks.map((b, j) => {
                  if (typeof b === "string") return <p key={j} className="leading-relaxed text-foreground/90">{b}</p>;
                  if (b.quote) return (
                    <p key={j} className="flex gap-2 rounded-xl border-l-2 border-secondary bg-accent/40 px-4 py-3 font-heading text-base font-medium text-primary">
                      <Quotes size={18} weight="fill" className="mt-0.5 shrink-0 text-secondary" /> {b.quote}
                    </p>
                  );
                  if (b.image) return (
                    <figure key={j} className="my-2 overflow-hidden rounded-2xl border border-border/60">
                      <img src={b.image} alt={b.caption} className="mx-auto block h-auto w-full max-h-[75vh] object-contain bg-muted/20" loading="lazy" />
                      <figcaption className="bg-muted/40 px-4 py-2 text-center text-xs text-muted-foreground">{b.caption}</figcaption>
                    </figure>
                  );
                  return (
                    <ul key={j} className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
                      {b.list.map((item, k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-foreground/90">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" /> {item}
                        </li>
                      ))}
                    </ul>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Principles */}
      <section className="mx-auto max-w-4xl px-5 py-14">
        <h2 className="text-center font-heading text-2xl font-medium tracking-tight text-primary">Tom's Personal Principles</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PRINCIPLES.map(([title, desc], i) => (
            <div key={i} className="flex gap-4 rounded-2xl border border-border/60 bg-card p-5" data-testid={`founder-principle-${i}`}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent font-heading text-sm font-semibold text-primary">{i + 1}</span>
              <div>
                <p className="font-heading font-medium text-foreground">{title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Personal vision statement */}
      <section className="mx-auto max-w-3xl px-5 pb-6">
        <div className="rounded-3xl border border-secondary/25 bg-accent/40 p-8 text-center">
          <Quotes size={26} weight="fill" className="mx-auto mb-3 text-secondary" />
          <p className="mx-auto max-w-2xl font-heading text-xl font-light leading-relaxed text-primary">
            “I want to spend my life imagining possibilities, learning what is necessary to make them real, and creating things that can help people, communities and the world.”
          </p>
          <p className="mt-4 font-heading text-base font-medium tracking-wide text-secondary">
            Imagine beyond what you know. Learn what you need. Build what you imagine.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">Tom (Thomas George) Vellaringattu — Founder</p>
        </div>
      </section>

      {/* Explore his ventures */}
      <section className="mx-auto max-w-4xl px-5 py-10">
        <p className="text-center text-sm font-medium uppercase tracking-wide text-muted-foreground">Explore his ventures</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {VENTURES.map((v) => (
            <a key={v.label} href={v.href} target="_blank" rel="noreferrer"
              className="group flex items-center justify-between rounded-2xl border border-border/60 bg-card p-5 transition-colors hover:border-secondary/40 hover:bg-accent/40"
              data-testid={`founder-venture-${v.label.split(".")[0].toLowerCase()}`}>
              <div>
                <p className="font-heading font-medium text-foreground">{v.label}<sup className="text-[10px]">{v.mark}</sup></p>
                <p className="mt-0.5 text-sm text-muted-foreground">{v.tag}</p>
              </div>
              <ArrowSquareOut size={20} className="text-muted-foreground transition-colors group-hover:text-secondary" />
            </a>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-3xl px-5 py-14">
        <div className="rounded-3xl bg-primary p-10 text-center text-primary-foreground">
          <HeartStraight size={30} weight="fill" className="mx-auto mb-4 text-secondary" />
          <p className="font-heading text-2xl font-light">Every idea begins as an imagination.</p>
          <p className="mx-auto mt-3 max-w-md text-primary-foreground/80">AlumniPulse is one of those ideas — a place where the people who shaped your journey are never lost to time.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {user ? (
              <Link to="/find"><Button size="lg" className="rounded-full gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="founder-cta-reconnect"><GraduationCap size={18} /> Reconnect with classmates</Button></Link>
            ) : (
              <Link to="/register"><Button size="lg" className="rounded-full gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="founder-cta-join"><Sparkle size={18} /> Join AlumniPulse</Button></Link>
            )}
            <Link to="/why-alumnipulse"><Button size="lg" variant="outline" className="rounded-full gap-2 border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10" data-testid="founder-cta-why">Why AlumniPulse <ArrowRight size={16} /></Button></Link>
          </div>
        </div>
      </section>
    </article>
  );
}
